import {
  asEligible,
  EligibilityStatus,
  Patient,
} from 'api/account/patients.type';
import { isAvailableUSState } from 'config/availableUSStates';
import { and, generate, not, or } from 'helpers/inference';
import { match, P } from 'ts-pattern';
import { isOver13, isUnder18 } from 'utils/dates';

import { isVideoPlan, PatientConsult } from './consults.types';

export enum CareCycleState {
  RequiresInsuranceEligibility = 1,
  NeedsAgreements,
  BeginAssessments,
  BeginMedical,
  Subscribe,
  AddCopay,
  Schedule,
  AddChildVideo,
  AddParentVideo,
  WaitingCarePlan,
  CarePlanAvailable,
  CannotTreat,
  UnavailableState,
}

const isTeenager = and<Patient>(
  p => isOver13(new Date(p.dateOfBirth)),
  p => isUnder18(new Date(p.dateOfBirth)),
);

const isNotAdult = (p: Patient) => isUnder18(new Date(p.dateOfBirth));
const isAdult = (p: Patient) => !isUnder18(new Date(p.dateOfBirth));

const needsGinaAuth = and(
  isNotAdult,
  (p: Patient) => p.requiresGinaAuthorization,
);

const needsTeenConsent = (p: Patient) => !p.teenConsent;

const needsToC = (p: Patient) => !p.termsAndConditionsSignedAt;

const needsMemberFinancialPolicy = (p: Patient) =>
  !p.memberFinancialPolicySignedAt;

const needsEligibilityCheck = (p: Patient) =>
  p.eligibilityStatus === EligibilityStatus.CheckNeeded;

const availableStateOfResidence = (p: Patient) => isAvailableUSState(p.state);

const needsConsentToCare = (p: Patient) => {
  return (
    typeof p.consentToTreatMinor !== 'undefined' &&
    p.consentToTreatMinor === null
  );
};

export interface InferredPatientState {
  isTeenager: boolean;
  needsGinaAuth: boolean;
  needsTeenConsent: boolean;
  needsEligibilityCheck: boolean;
  availableStateOfResidence: boolean;
  needsConsentToCare: boolean;
  needsToC: boolean;
  needsMemberFinancialPolicy: boolean;
}

export const generatePatientState = generate<[Patient], InferredPatientState>({
  isTeenager,
  needsGinaAuth,
  needsTeenConsent: and(isTeenager, needsTeenConsent),
  needsEligibilityCheck,
  availableStateOfResidence,
  needsConsentToCare,
  needsToC,
  needsMemberFinancialPolicy,
});

export interface InferredConsultState {
  isIntake: boolean;
  isVideoPlan: boolean;
  assessmentsCompleted: boolean;
  medHistoryCompleted: boolean;
  hasParentVideo: boolean;
  hasMemberVideo: boolean;
  needsCopay: boolean;
  needsSubscription: boolean;
  hasCarePlan: boolean;
  onboardingCompleted: boolean;
}

const isIntake = (c: PatientConsult) => !!c.isBaseline;

const isVideoConsult = (c: PatientConsult) => isVideoPlan(c.subscribedPlan);

const assessmentsCompleted = (c: PatientConsult) =>
  c.patientState !== 'patient_initialized' &&
  (c.patientState === 'patient_completed' ||
    c.symptomAssessmentSurveyResponses?.every(sr => !!sr.completedAt));

const medHistoryCompleted = (c: PatientConsult) =>
  c.patientState === 'patient_completed' ||
  !!c.medicalHistorySurveyResponse?.completedAt;

const hasParentVideo = (c: PatientConsult) =>
  c.videos.some(v => v.videoSubject !== 'child');
const hasMemberVideo = (c: PatientConsult) =>
  c.videos.some(
    v =>
      v.videoSubject === 'child' ||
      v.videoSubject === 'all' ||
      (isAdult(c.patient) && v.videoSubject === 'self'),
  );

const canSkipCC = (c: PatientConsult) => c.skipCreditCard;
const isEligible = (c: PatientConsult) =>
  asEligible(c.patient.eligibilityStatus) &&
  c.patient.insuranceCarrier !== 'None';

const needsCopay = and(
  isEligible,
  not(canSkipCC),
  c => c.paymentState !== 'paid',
);
const needsSubscription = and(
  not(isEligible),
  c =>
    ['unpaid', 'patient_declined', 'payment_failed'].indexOf(c.paymentState) >
    -1,
);
const hasCarePlan = (c: PatientConsult) => !!c.pdfUrl;

export const onboardingCompleted = or(
  not(isIntake),
  and(
    assessmentsCompleted,
    medHistoryCompleted,
    not(needsCopay),
    not(needsSubscription),
  ),
);

export const generateConsultState = generate<
  [PatientConsult],
  InferredConsultState
>({
  isIntake,
  isVideoPlan: isVideoConsult,
  assessmentsCompleted,
  medHistoryCompleted,
  hasParentVideo,
  hasMemberVideo,
  needsCopay,
  needsSubscription,
  hasCarePlan,
  onboardingCompleted,
});

export const getConsultCareCycleStep = (
  consult: PatientConsult,
  isMemberFinancialPolicyEnabled: boolean,
  patient?: Patient,
) => {
  const p = generatePatientState(patient ?? consult.patient);
  const c = generateConsultState(consult);

  let state = match([p, c, isMemberFinancialPolicyEnabled])
    .with(
      [{ availableStateOfResidence: false }, P._, P._],
      () => CareCycleState.UnavailableState,
    )
    .with(
      [{ needsEligibilityCheck: true }, P._, P._],
      () => CareCycleState.RequiresInsuranceEligibility,
    )
    .with(
      [
        { needsGinaAuth: true },
        { isIntake: true, assessmentsCompleted: false },
        P._,
      ],
      () => CareCycleState.NeedsAgreements,
    )
    .with(
      [
        { needsTeenConsent: true },
        { isIntake: true, assessmentsCompleted: false },
        P._,
      ],
      () => CareCycleState.NeedsAgreements,
    )
    .with(
      [
        { needsToC: true },
        { isIntake: true, assessmentsCompleted: false },
        P._,
      ],
      () => CareCycleState.NeedsAgreements,
    )
    .with(
      [
        { needsMemberFinancialPolicy: true },
        { isIntake: true, assessmentsCompleted: false },
        true,
      ],
      () => CareCycleState.NeedsAgreements,
    )
    .with(
      [
        { needsConsentToCare: true, isNotAdult: true },
        { isIntake: true, assessmentsCompleted: false },
        P._,
      ],
      () => CareCycleState.NeedsAgreements,
    )
    .otherwise(() => null);

  if (state) return state;

  state = match(c)
    .with(
      { assessmentsCompleted: false },
      () => CareCycleState.BeginAssessments,
    )
    .with({ medHistoryCompleted: false }, () => CareCycleState.BeginMedical)
    .with({ needsCopay: true }, () => CareCycleState.AddCopay)
    .with({ needsSubscription: true }, () => CareCycleState.Subscribe)
    .with(
      { isVideoPlan: true, hasMemberVideo: false, isIntake: false },
      () => CareCycleState.AddChildVideo,
    )
    .with(
      {
        isVideoPlan: true,
        hasMemberVideo: true,
        hasParentVideo: false,
        isIntake: false,
      },
      () => CareCycleState.AddParentVideo,
    )
    .with({ hasCarePlan: true }, () => CareCycleState.CarePlanAvailable)
    .with(
      { hasCarePlan: false, isIntake: false },
      () => CareCycleState.WaitingCarePlan,
    )
    .with({ hasCarePlan: false, isIntake: true }, () => CareCycleState.Schedule)
    .otherwise(() => null);

  return state;
};
